/**
 * MilestoneTemplateModal - マイルストーンテンプレート管理モーダル
 *
 * チーム共通または個人用のマイルストーンテンプレートを管理
 * - テンプレートの一覧表示
 * - テンプレートの追加・編集・削除
 * - 個人プロジェクト用テンプレートにも対応
 */

import { useState, useEffect } from 'react'
import {
  X,
  Flag,
  Plus,
  Edit3,
  Trash2,
} from 'lucide-react'

// マイルストーンテンプレート型
export type MilestoneTemplate = {
  id: string
  teamId: string
  title: string
  iconType: 'emoji' | 'lucide'
  iconValue: string
  color: string
  sortOrder: number
}

// 利用可能な絵文字アイコン
const EMOJI_ICONS = ['📋', '✅', '🎬', '🚀', '📝', '🎯', '👀', '🎉', '👥', '⏰', '📸', '🔔', '⭐', '💡', '🎨', '📊']

// 利用可能な色
const TEMPLATE_COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16']

type ModalMode = 'list' | 'add' | 'edit'

interface MilestoneTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  templates: MilestoneTemplate[]
  teamName?: string
  isPersonal?: boolean  // 個人用テンプレートモード
  onAdd: (template: Partial<MilestoneTemplate>) => void
  onUpdate: (templateId: string, updates: Partial<MilestoneTemplate>) => void
  onDelete: (templateId: string) => void
}

export default function MilestoneTemplateModal({
  isOpen,
  onClose,
  templates,
  teamName,
  isPersonal = false,
  onAdd,
  onUpdate,
  onDelete,
}: MilestoneTemplateModalProps) {
  const [mode, setMode] = useState<ModalMode>('list')
  const [editingTemplate, setEditingTemplate] = useState<MilestoneTemplate | null>(null)

  // フォーム状態
  const [title, setTitle] = useState('')
  const [selectedIcon, setSelectedIcon] = useState('📋')
  const [selectedColor, setSelectedColor] = useState('#6366f1')

  // モーダルが開いたらリセット
  useEffect(() => {
    if (isOpen) {
      setMode('list')
      setEditingTemplate(null)
      resetForm()
    }
  }, [isOpen])

  const resetForm = () => {
    setTitle('')
    setSelectedIcon('📋')
    setSelectedColor('#6366f1')
  }

  const startAdd = () => {
    resetForm()
    setMode('add')
  }

  const startEdit = (template: MilestoneTemplate) => {
    setEditingTemplate(template)
    setTitle(template.title)
    setSelectedIcon(template.iconValue)
    setSelectedColor(template.color)
    setMode('edit')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    if (mode === 'add') {
      onAdd({
        title: title.trim(),
        iconType: 'emoji',
        iconValue: selectedIcon,
        color: selectedColor,
        sortOrder: templates.length,
      })
    } else if (mode === 'edit' && editingTemplate) {
      onUpdate(editingTemplate.id, {
        title: title.trim(),
        iconValue: selectedIcon,
        color: selectedColor,
      })
    }

    setMode('list')
    resetForm()
    setEditingTemplate(null)
  }

  const handleDelete = (templateId: string) => {
    if (confirm('このテンプレートを削除しますか？\n※配置済みのマイルストーンは削除されません')) {
      onDelete(templateId)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b bg-indigo-50">
          <div className="flex items-center gap-2">
            <Flag className="w-5 h-5 text-indigo-600" />
            <h3 className="font-bold text-lg">
              {mode === 'list' && 'マイルストーンテンプレート'}
              {mode === 'add' && 'テンプレート追加'}
              {mode === 'edit' && 'テンプレート編集'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-indigo-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* チーム名/個人用表示 */}
        {mode === 'list' && (
          <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600 border-b">
            {isPersonal ? (
              <span>個人用テンプレート</span>
            ) : teamName ? (
              <>チーム: <span className="font-medium">{teamName}</span></>
            ) : null}
          </div>
        )}

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto">
          {mode === 'list' && (
            <div className="p-4">
              {/* 追加ボタン */}
              <button
                onClick={startAdd}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors mb-4"
              >
                <Plus className="w-5 h-5" />
                <span>新しいテンプレートを追加</span>
              </button>

              {/* テンプレート一覧 */}
              {templates.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  テンプレートがありません
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 group"
                    >
                      <span className="text-2xl">{template.iconValue}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" style={{ color: template.color }}>
                          {template.title}
                        </div>
                      </div>
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: template.color }}
                      />
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => startEdit(template)}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="編集"
                        >
                          <Edit3 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-1.5 hover:bg-red-100 rounded"
                          title="削除"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(mode === 'add' || mode === 'edit') && (
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* タイトル */}
              <div>
                <label className="block text-sm font-medium mb-1">名称 *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="例: 台本チェック、納品など"
                  autoFocus
                  required
                />
              </div>

              {/* アイコン選択 */}
              <div>
                <label className="block text-sm font-medium mb-2">アイコン</label>
                <div className="flex flex-wrap gap-2">
                  {EMOJI_ICONS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setSelectedIcon(emoji)}
                      className={`w-10 h-10 flex items-center justify-center text-xl rounded-lg border-2 transition-all ${
                        selectedIcon === emoji
                          ? 'border-indigo-500 bg-indigo-50 scale-110'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* 色選択 */}
              <div>
                <label className="block text-sm font-medium mb-2">色</label>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATE_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSelectedColor(color)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        selectedColor === color
                          ? 'border-gray-800 scale-110 ring-2 ring-offset-2'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color, '--tw-ring-color': color } as React.CSSProperties}
                    />
                  ))}
                </div>
              </div>

              {/* プレビュー */}
              <div className="bg-gray-50 rounded-lg p-3">
                <label className="block text-xs text-gray-500 mb-2">プレビュー</label>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{selectedIcon}</span>
                  <div className="font-medium" style={{ color: selectedColor }}>
                    {title || 'テンプレート名'}
                  </div>
                </div>
              </div>

              {/* ボタン */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setMode('list')
                    resetForm()
                    setEditingTemplate(null)
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  戻る
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  {mode === 'add' ? '追加' : '保存'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* フッター（一覧モードのみ） */}
        {mode === 'list' && (
          <div className="px-4 py-3 border-t bg-gray-50 text-xs text-gray-400">
            {isPersonal
              ? '※個人用テンプレートはあなた専用です'
              : '※テンプレートはチーム内で共有されます'
            }
          </div>
        )}
      </div>
    </div>
  )
}
